/**
 * Lista de 03/11 (ficha `demandas/entrada/2026-11-03_malgo_FUN_portfolio-por-vertical-na-proposta.md`, "Regra final"): ao abrir o
 * e-mail da aba E-mails ou da proposta, o Geral e o portfólio de cada vertical que NÃO está na negociação já vêm marcados. Regras puras (a tela só desenha o resultado):
 * a ordem, o que cabe, o que falta e o que não coube. Nada sai sozinho: a pessoa vê e tira qualquer um antes de enviar, e o
 * servidor confere tudo de novo (quantidade, tamanho de cada arquivo, soma, visibilidade).
 */
import type { ArquivoCentral } from './CentralArquivos';

/** Teto da SOMA dos anexos (28 MiB: o Resend aceita 40 MB em base64, cerca de 30 MB de arquivo) e por arquivo (25 MiB). */
export const LIMITE_SOMA_ANEXOS = 28 * 1024 * 1024;
export const LIMITE_ARQUIVO_ANEXO = 25 * 1024 * 1024;
/**
 * Folga para o PDF da proposta, que só nasce no servidor (o tamanho não é conhecido na tela). Uma proposta comum tem poucas
 * centenas de kB; 2 MiB cobre as grandes. Se o PDF vier maior, o servidor recusa com `email.anexo_grande` e a pessoa tira um.
 */
export const FOLGA_PDF_PROPOSTA = 2 * 1024 * 1024;

export interface OpcaoPortfolio {
  tipo: 'vertical' | 'geral';
  empresa_id: string | null;
  nome: string | null;
  arquivo: (Pick<ArquivoCentral, 'id' | 'nome' | 'mime' | 'tamanho' | 'compartilhado' | 'texto_modelo' | 'created_at'> & { autor_nome?: string | null }) | null;
}

/** Chave estável da opção (a vertical pelo id; o geral por "geral"). */
export const chaveDaOpcao = (o: Pick<OpcaoPortfolio, 'tipo' | 'empresa_id'>) => (o.tipo === 'geral' ? 'geral' : String(o.empresa_id));

export type SituacaoPortfolio =
  /** Marcado: o arquivo entra nos anexos (a pessoa pode tirar). */
  | 'marcado'
  /** A opção não tem arquivo na Central (ou a pessoa não o vê): caixa desabilitada, com o aviso de onde subir. */
  | 'sem_arquivo'
  /** A vertical está na negociação (ou não deu para ler as dela): não vem marcado, mas a pessoa pode marcar. */
  | 'da_negociacao'
  /** Tem arquivo, mas não coube no teto de quantidade ou de tamanho: fica desmarcado, com aviso. */
  | 'nao_coube';

export interface LinhaPortfolio {
  chave: string;
  opcao: OpcaoPortfolio;
  situacao: SituacaoPortfolio;
}

export interface EntradaSelecao {
  /** As verticais da negociação: as do cadastro dela e as dos itens (`todas` = marcada em "Todas"); null = não deu para ler: só o Geral vem marcado. */
  verticaisDaNegociacao: { todas: boolean; ids: readonly (string | null | undefined)[] } | null;
  /** Quantos anexos a lista já tem (arquivos subidos, da negociação, da Central) e quanto pesam. */
  quantidadeAtual?: number;
  bytesAtuais?: number;
  /** Teto de quantidade do envio (com a proposta, o do PDF já descontado). */
  maxAnexos: number;
  /** Teto da soma e reserva do PDF da proposta (omitir = os padrões desta tela). */
  limite?: number;
  folga?: number;
}

/**
 * Ordem: o Geral primeiro (vai em todo e-mail e tem prioridade no limite), depois as verticais, na ordem da lista. A vertical
 * que está na negociação não vem marcada (`da_negociacao`), mas a pessoa pode marcar; em "Todas", só o Geral. As outras são marcadas enquanto couber em
 * `maxAnexos` e no teto da soma já descontada a folga do PDF; a que não cabe, ou ultrapassa o teto por arquivo, fica `nao_coube`
 * (e a seguinte, que pode ser menor, ainda tenta). Não há controle do que já foi enviado.
 */
export function selecionarPortfolios(opcoes: readonly OpcaoPortfolio[], e: EntradaSelecao): LinhaPortfolio[] {
  const daNegociacao = new Set((e.verticaisDaNegociacao?.ids ?? []).flatMap((x) => (x ? [String(x).toLowerCase()] : [])));
  const temTudo = !e.verticaisDaNegociacao || e.verticaisDaNegociacao.todas;
  const ordem = [...opcoes.filter((o) => o.tipo === 'geral'), ...opcoes.filter((o) => o.tipo === 'vertical')];

  const teto = (e.limite ?? LIMITE_SOMA_ANEXOS) - (e.folga ?? FOLGA_PDF_PROPOSTA);
  let quantidade = e.quantidadeAtual ?? 0;
  let bytes = e.bytesAtuais ?? 0;
  return ordem.map((opcao) => {
    const chave = chaveDaOpcao(opcao);
    const a = opcao.arquivo;
    if (!a) return { chave, opcao, situacao: 'sem_arquivo' as const };
    if (opcao.tipo === 'vertical' && (temTudo || daNegociacao.has(String(opcao.empresa_id).toLowerCase()))) return { chave, opcao, situacao: 'da_negociacao' as const };
    if (quantidade + 1 > e.maxAnexos || bytes + a.tamanho > teto || a.tamanho > LIMITE_ARQUIVO_ANEXO) return { chave, opcao, situacao: 'nao_coube' as const };
    quantidade += 1;
    bytes += a.tamanho;
    return { chave, opcao, situacao: 'marcado' as const };
  });
}

/** Os arquivos marcados, na ordem (para a lista `central` do envio). */
export const arquivosMarcados = (linhas: readonly LinhaPortfolio[]): ArquivoCentral[] =>
  linhas.filter((l) => l.situacao === 'marcado' && l.opcao.arquivo).map((l) => ({ autor_nome: null, ...l.opcao.arquivo! }) as ArquivoCentral);

/**
 * Pré-marcação UMA vez só: devolve o que entra na lista agora, ou null se já foi feita (a consulta atualiza, a pessoa reabre
 * "Arquivos da Central": o que ela tirou não volta). `jaFeita` é a marca guardada pela tela.
 */
export function prePortfolio(jaFeita: boolean, linhas: readonly LinhaPortfolio[]): ArquivoCentral[] | null {
  return jaFeita ? null : arquivosMarcados(linhas);
}

/** Os links do texto viram `<a>` no HTML do editor (o texto vem de `htmlDeTexto`, que já escapou aspas e `<`). */
export function linkarEnderecos(html: string): string {
  return html.replace(/https?:\/\/[^\s<>"']+/g, (url) => {
    const fim = /[.,;:!?)\]]+$/.exec(url)?.[0] ?? '';
    const limpo = fim ? url.slice(0, -fim.length) : url;
    return limpo ? `<a href="${limpo}">${limpo}</a>${fim}` : url;
  });
}

/**
 * "Conheça nossos outros serviços" no texto do e-mail (aba E-mails e proposta): entra antes do último parágrafo (a assinatura), separado
 * por linha em branco. Vazio ou só espaços = o texto não muda.
 */
export function comOutrosServicos(texto: string, outros: string): string {
  const extra = outros.trim();
  if (!extra) return texto;
  const corte = texto.lastIndexOf('\n\n');
  return corte < 0 ? `${texto.trim()}\n\n${extra}` : `${texto.slice(0, corte).trimEnd()}\n\n${extra}${texto.slice(corte)}`;
}
